/**
 * DEC-154 Learn capture — arm, watch OPBridge's inbound traffic monitor,
 * resolve the newest capturable event. Pure logic, no Solid state —
 * `MidiTriggerLearn.tsx` owns the arm/cancel UI and calls this directly.
 *
 * ─── Why the TRAFFIC MONITOR and not `/midi/recent` (changed 2026-08-27) ───
 *
 * Learn used to drain `GET /midi/recent`. That route POPS its ring, so it has
 * exactly one consumer: whoever reads first takes the message. Once inbound
 * MIDI got a real runtime consumer (`opbridge/midiInPoll.ts`, feeding
 * StatusLed), the two raced — the background poll ate the operator's stomp and
 * Learn timed out having "seen nothing" while the pedal, the daemon and the
 * ring were all working perfectly. That is close to unattributable from
 * outside.
 *
 * `GET /traffic/midi/recent?direction=in` is non-destructive, carries every
 * port, and is already granted to our bearer. Reading it here removes the race
 * outright instead of coordinating around it (the `suspendMidiInPoll` dance
 * this replaced, now deleted).
 *
 * ⚠️ **DESK-TIME dependency only, and that distinction is load-bearing.** The
 * traffic monitor is an OBSERVABILITY surface: OPBridge may later sample,
 * rate-limit or perf-gate it (they already keep 30 Hz `mixer_update` out of
 * the consumer ring for exactly that reason), and the operator can denylist a
 * source. Nothing that must survive a gig may depend on it — StatusLed
 * deliberately does NOT, and rides the delivery plane instead. Learn only ever
 * runs at a desk while authoring, so a monitor that degrades under load costs
 * nothing here. Prior art for the split: k8s `metrics-server` declares itself
 * invalid for non-autoscaling consumers because accuracy was traded for
 * responsiveness; AWS "static stability" says never make the serving path
 * depend on a plane that is allowed to degrade.
 *
 * ─── Port scope (widened 2026-08-27) ───
 *
 * Learn used to accept ONLY `OPButtons-Control`, so a controller plugged
 * straight in was invisible to it — the operator had to route it through a
 * virtual port first, which no novice would deduce. Any port is accepted now.
 * Safe because `ruleSync.ts::conditionFor` emits `{op:'portEquals', name:
 * b.port}`, so the rule OPBridge matches carries whatever port was captured: a
 * binding learned from `Midi Fighter Twister` fires from that port verbatim.
 * `MIDI_TRIGGER_PORT` stays the RECOMMENDED port for an Ableton-mirror setup
 * and is what the "nothing seen" hint points at.
 *
 * CHANNEL CONVENTION, deliberate: `match.channel` is the RAW 0-based MIDI
 * channel (0-15) decoded from the status byte's low nibble — NOT remapped to
 * the app's 1-based display convention. The captured value is pushed VERBATIM
 * into OPBridge's `channelEquals.value` (`ruleSync.ts`), which compares
 * against the same raw nibble, so the two agree by construction. Translating
 * here and back at push time is exactly the silent off-by-one this project has
 * hit before (`feedback_item_channel_base_inconsistency`). UI adds 1 at render
 * time only.
 */

import type { TrafficMidiEntry } from '../opbridge/httpClient.ts';
import { MIDI_TRIGGER_PORT } from './constants.ts';

export interface CapturedTrigger {
  port: string;
  match: { type: 'note' | 'cc'; number: number; channel: number };
}

/** The port to suggest when Learn saw nothing — still the right answer for an
 *  Ableton-mirror rig, just no longer a hard filter. */
export const RECOMMENDED_TRIGGER_PORT = MIDI_TRIGGER_PORT;

/**
 * Decode one traffic envelope into a capturable trigger, or null.
 *
 * ⚠️ Decodes from RAW `bytes`, which is why this is safer than the
 * `/midi/recent` shape it replaced. That route splits the status byte for you
 * (`status` pre-masked, `channel` alongside), and the previous implementation
 * re-derived the channel as `status & 0x0f` — which is 0 for every message on
 * every channel. Here `bytes[0]` still carries the nibble, so the decode is
 * the unambiguous textbook one and cannot drift from the wire.
 *
 * Capture-worthy = note-ON with real velocity (velocity-0 note-on is MIDI's
 * own note-off convention, ignored, same as an explicit 0x8n) or any
 * control-change. Everything else (note-off, pitch-bend, aftertouch, sysex,
 * real-time) is not a momentary/toggle trigger shape DEC-154 targets.
 */
function parseTrafficEntry(e: TrafficMidiEntry): CapturedTrigger['match'] | null {
  // A denylisted source is dropped at OPBridge's EventBus boundary before it
  // reaches engines OR RULES (`config.rs`'s `denylist` doc). Learning from one
  // would author a binding whose rule can never fire - a control that looks
  // bound and is silent forever. Skip rather than capture.
  if (e.filtered) return null;
  const bytes = e.bytes;
  if (!Array.isArray(bytes) || bytes.length < 3) return null;
  const status = bytes[0]!;
  const data1 = bytes[1]!;
  const data2 = bytes[2]!;
  const hi = status & 0xf0;
  const channel = status & 0x0f;
  if (hi === 0x90 && data2 > 0) return { type: 'note', number: data1, channel };
  if (hi === 0xb0) return { type: 'cc', number: data1, channel };
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

/** Highest `seq` in a snapshot, or 0 when empty. */
function maxSeq(entries: TrafficMidiEntry[]): number {
  let m = 0;
  for (const e of entries) {
    if (typeof e.seq === 'number' && e.seq > m) m = e.seq;
  }
  return m;
}

/**
 * Poll for a capturable event, newest-first within each poll (the operator's
 * most recent press wins if several landed). Real poll-for-the-condition, not
 * sleep-then-assert — resolves the instant a match lands.
 *
 * ⚠️ **Watermarked on `seq`, and that is REQUIRED, not an optimisation.** The
 * traffic ring is NON-destructive: unlike the drain this replaced, the very
 * first poll returns up to 50 messages that were already sitting there,
 * possibly minutes old. Without a watermark Learn would "capture" a stale
 * event instantly, before the operator touched anything — silently binding the
 * wrong control, with a confident success toast and no sign anything was
 * wrong. The arming snapshot sets the high-water mark; only strictly-newer
 * entries are considered.
 *
 * `seq` resets on a daemon restart (the ring is in-memory), so a snapshot
 * whose max sits BELOW the watermark means the daemon restarted rather than
 * time running backwards — the watermark re-bases instead of staying
 * permanently unreachable, which would disable Learn until a page reload.
 */
export async function pollForMidiTrigger(
  client: { trafficMidiRecent(): Promise<TrafficMidiEntry[]> },
  opts: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal } = {},
): Promise<CapturedTrigger | null> {
  const timeoutMs = opts.timeoutMs ?? 15000;
  const intervalMs = opts.intervalMs ?? 300;
  const deadline = Date.now() + timeoutMs;

  // Establish the watermark BEFORE listening. On a transient failure we fall
  // through with 0 rather than refusing to arm: the cost is that the first
  // poll may consider pre-existing entries, which is strictly better than
  // Learn appearing broken because the bridge blipped at arm time.
  let watermark = 0;
  try {
    watermark = maxSeq(await client.trafficMidiRecent());
  } catch {
    // fall through with watermark 0
  }

  while (Date.now() < deadline) {
    if (opts.signal?.aborted) return null;
    try {
      const entries = await client.trafficMidiRecent();
      const highest = maxSeq(entries);
      if (highest < watermark) watermark = 0;   // daemon restarted, seq re-based
      for (let i = entries.length - 1; i >= 0; i--) {
        const e = entries[i];
        if (!e || typeof e.seq !== 'number' || e.seq <= watermark) continue;
        const match = parseTrafficEntry(e);
        if (match) return { port: e.port, match };
      }
    } catch {
      // Transient bridge hiccup mid-poll — swallow and retry next tick,
      // matching every other never-throws poll loop in this codebase.
    }
    if (opts.signal?.aborted) return null;
    await sleep(intervalMs);
  }
  return null;
}
